import { Alert, Button, Checkbox, DatePicker, Form, FormInstance, Input, InputNumber, Select } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { apiGetListPackagesConnection } from '../../api'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { IPartnerPackage } from '../../models/package.modal'
import { TRANSACTION_CONNECTION_TYPE_CONNECTION, TRANSACTION_CONNECTION_TYPE_EXTENSION } from '../../assets/constants/default_params'

type Props = {
    form: FormInstance
    outletId: string
    cpCode: string
    onSubmit: (values: any) => void
    onCancel?: () => void
}

const fmtVND = (v?: number | null) => {
    if (v == null) return '—'
    return v.toLocaleString('vi-VN') + ' ₫'
}

export default function PricingForm({ form, outletId, cpCode, onSubmit, onCancel }: Props) {
    const [packages, setPackages] = useState<IPartnerPackage[]>([])
    const selectedId = Form.useWatch('partnerPackageId', form)

    useEffect(() => {
        if (!cpCode) {
            setPackages([])
            return
        }
        let mounted = true
        apiGetListPackagesConnection(cpCode).then((resp: any) => {
            if (!mounted) return
            setPackages(resp?.data?.partner_packages ?? [])
        }).catch(() => {
            if (mounted) setPackages([])
        })
        return () => { mounted = false }
    }, [cpCode])

    const packageOptions = useMemo(() => packages.map(p => {
        let suffix = ''
        if (p.type === TRANSACTION_CONNECTION_TYPE_CONNECTION) suffix = ' — Đấu mới'
        else if (p.type === TRANSACTION_CONNECTION_TYPE_EXTENSION) suffix = ' — Gia hạn'
        return { label: `${p.name}${suffix}`, value: Number(p.id) }
    }), [packages])

    const selectedPackage = useMemo(
        () => packages.find(p => Number(p.id) === Number(selectedId)),
        [packages, selectedId],
    )
    const defaultPrice = selectedPackage?.price != null ? Number(selectedPackage.price) : undefined
    const defaultCommission = selectedPackage?.price_cashback != null ? Number(selectedPackage.price_cashback) : undefined

    const handleFinish = (values: any) => {
        onSubmit({
            outletId,
            partnerPackageId: Number(values.partnerPackageId),
            agreedPrice: values.agreedPrice != null ? Number(values.agreedPrice) : undefined,
            agreedCommission: values.agreedCommission != null ? Number(values.agreedCommission) : undefined,
            agreedNote: values.agreedNote || '',
            effectiveFrom: dayjs(values.effectiveFrom).format('YYYY-MM-DD'),
            effectiveTo: values.effectiveTo ? dayjs(values.effectiveTo).format('YYYY-MM-DD') : undefined,
            closeActive: !!values.closeActive,
        })
    }

    return (
        <Form
            form={form}
            layout='vertical'
            onFinish={handleFinish}
            autoComplete='off'
            initialValues={{ closeActive: true, effectiveFrom: dayjs() }}
        >
            <Form.Item name='partnerPackageId' label='Gói cước đối tác' rules={[{ required: true, message: 'Vui lòng chọn gói!' }]}>
                <Select
                    showSearch
                    options={packageOptions}
                    filterOption={filterOptionByLabel}
                    placeholder={cpCode ? 'Chọn gói cước' : 'Đại lý chưa có cpCode — vui lòng cập nhật'}
                    disabled={!cpCode}
                />
            </Form.Item>
            {selectedPackage && (
                <Alert
                    type='info'
                    showIcon
                    style={{ marginBottom: 16 }}
                    message='Giá mặc định của gói'
                    description={
                        <div>
                            <div>Giá: <b>{fmtVND(defaultPrice)}</b></div>
                            <div>Hoa hồng: <b>{fmtVND(defaultCommission)}</b></div>
                            <div style={{ marginTop: 4, color: '#888' }}>Bỏ trống 2 ô bên dưới để áp dụng mặc định.</div>
                        </div>
                    }
                />
            )}
            <Form.Item name='agreedPrice' label='Giá thỏa thuận' tooltip='Bỏ trống nếu áp dụng giá mặc định của gói.'>
                <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder={defaultPrice != null ? `Mặc định: ${defaultPrice.toLocaleString('vi-VN')}` : 'Giá override'}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
            </Form.Item>
            <Form.Item name='agreedCommission' label='Hoa hồng thỏa thuận' tooltip='Bỏ trống nếu áp dụng hoa hồng mặc định của gói.'>
                <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder={defaultCommission != null ? `Mặc định: ${defaultCommission.toLocaleString('vi-VN')}` : 'Hoa hồng override'}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
            </Form.Item>
            <Form.Item name='effectiveFrom' label='Áp dụng từ' rules={[{ required: true, message: 'Vui lòng chọn ngày!' }]}>
                <DatePicker style={{ width: '100%' }} format='DD/MM/YYYY' />
            </Form.Item>
            <Form.Item name='effectiveTo' label='Áp dụng đến (tùy chọn)'>
                <DatePicker style={{ width: '100%' }} format='DD/MM/YYYY' />
            </Form.Item>
            <Form.Item name='closeActive' valuePropName='checked' tooltip='Nếu bật, thỏa thuận đang hiệu lực (cùng gói) sẽ được đóng lại tự động.'>
                <Checkbox>Đóng thỏa thuận cũ (cùng gói)</Checkbox>
            </Form.Item>
            <Form.Item name='agreedNote' label='Ghi chú'>
                <Input.TextArea rows={2} />
            </Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {onCancel && <Button onClick={onCancel}>Huỷ</Button>}
                <Button type='primary' htmlType='submit'>Thêm</Button>
            </div>
        </Form>
    )
}
